import Component from '@bythepixel/component-loader';

export default class SearchHero extends Component {
  static selector = '[data-comp-search-hero]'

  constructor(containerEl) {
    super(containerEl);
    this.formEl = this.$container.querySelector('[data-search-form]');
    this.dropdowns = this.$container.querySelectorAll('[data-dropdown-type]');
    this.submitButtonEl = this.$container.querySelector('[data-search-hero-form-submit]');
    this.disabledCssClass = 'search-hero__select-container--disabled';

    // Used on pages that have filters, like the make page
    this.filterId = this.$container.dataset.filterId;
    this.storeUrl = this.getCleanStoreUrl();

    // Session storage variables
    this.sessionMake = sessionStorage.getItem('make');
    this.sessionModel = sessionStorage.getItem('model');
    this.sessionType = sessionStorage.getItem('type');

    this.bindEvents();
  }

  bindEvents() {
    this.pageLoadEvents();
    this.dropdownEvents();
    this.submitEvents();
  }

  getCleanStoreUrl() {
    const { searchUrl } = this.$container.dataset;
    if (searchUrl === undefined) {
      return undefined;
    }

    return searchUrl.replace(/&amp;/g, '&').replace(/&quot;/g, '"');
  }

  disableDropdown(selectEl) {
    selectEl.parentNode.classList.add(this.disabledCssClass);
    // eslint-disable-next-line no-param-reassign
    selectEl.disabled = true;
  }

  /**
   * This handles the selected dropdown values, and populates the next dropdowns based on the previous selection
   */
  dropdownEvents() {
    if (this.dropdowns.length > 0) {
      this.dropdowns.forEach((dropdown, index) => {
        dropdown.addEventListener('change', async() => {
          this.updateDropdownSelections();

          // Dependent on functions above
          await this.populateDropdown(dropdown, this.dropdowns[index + 1]);
          this.updateSessionVariables();
        });
      });
    }
  }

  enableDropdown(selectEl) {
    selectEl.parentNode.classList.remove(this.disabledCssClass);
    // eslint-disable-next-line no-param-reassign
    selectEl.disabled = false;
  }

  async fetchMakes() {
    const response = await fetch(`/api/v1/faceted/makes/names?${this.getQueryString()}`, {
      method: 'get',
    });

    if (response.status !== 200) {
      console.warn('Not able to fetch makes');
      return [];
    }

    return response.json();
  }

  async fetchModels() {
    const response = await fetch(`/api/v1/faceted/models/names?${this.getQueryString()}`, {
      method: 'get',
    });

    if (response.status !== 200) {
      console.warn('Not able to fetch models');
      return [];
    }

    return response.json();
  }

  getQueryString() {
    const values = [];
    if (this.selectedMake && this.selectedMake !== 'undefined') {
      values.push(`make=${this.selectedMake}`);
    }

    if (this.selectedModel && this.selectedModel !== 'undefined') {
      values.push(`model=${this.selectedModel}`);
    }

    if (this.selectedType && this.selectedType !== 'undefined') {
      values.push(`type=${this.selectedType}`);
    }

    if (this.filterId) {
      values.push(`filterId=${this.filterId}`);
    }

    return values.join('&');
  }

  getQueryStringForFacetedSearch() {
    const values = [];
    if (this.selectedMake && this.selectedMake !== 'undefined') {
      values.push(`makes=["${this.selectedMake}"]`);
    }

    if (this.selectedModel && this.selectedModel !== 'undefined') {
      values.push(`models=["${this.selectedModel}"]`);
    }

    if (this.selectedType && this.selectedType !== 'undefined') {
      values.push(`vehicleTypes=["${this.selectedType}"]`);
    }

    return values.join('&');
  }

  /**
   * If the user clicked search, then goes back to the search page
   * This function retrieves session variables or preselected values so that the dropdowns retain the selected values
   */
  pageLoadEvents() {
    this.selectedMake = this.sessionMake ?? undefined;
    this.selectedModel = this.sessionModel ?? undefined;
    this.selectedType = this.sessionType ?? undefined;

    const typeDropdown = this.$container.querySelector('[data-dropdown="type"]');
    const makeDropdown = this.$container.querySelector('[data-dropdown="make"]');
    const modelDropdown = this.$container.querySelector('[data-dropdown="model"]');

    if (this.selectedType && typeDropdown) {
      typeDropdown.value = this.selectedType;
    }

    if (this.selectedMake && makeDropdown) {
      // Repopulate the dropdowns with either preselected values or session variable values
      (async () => {
        const options = await this.fetchMakes();
        this.populateDropdownOptions(makeDropdown, options);
        makeDropdown.value = this.selectedMake;
      })();
    }

    if (this.selectedModel && modelDropdown) {
      // Repopulate the dropdowns with either preselected values or session variable values
      (async () => {
        const options = await this.fetchModels();
        this.populateDropdownOptions(modelDropdown, options);
        modelDropdown.value = this.selectedModel;
      })();
    }
  }

  submitEvents() {
    if (!this.formEl || !this.submitButtonEl) {
      console.warn('Search is not available');
      return;
    }

    this.formEl.addEventListener('submit', async(e) => {
      // Stop default submission behavior
      e.preventDefault();

      if (this.storeUrl) {
        window.location = `${this.storeUrl}&${this.getQueryStringForFacetedSearch()}`;
      } else {
        window.location = `/store/type-all?${this.getQueryStringForFacetedSearch()}`;
      }
    });
  }

  async populateDropdown(dropdown, dropdownToBePopulated) {
    if (dropdownToBePopulated === undefined) {
      return;
    }

    this.submitButtonEl.disabled = true;

    const { dropdownType } = dropdownToBePopulated.dataset;

    let options = [];
    // Fetch dropdown options
    if (dropdownType === 'make') {
      options = await this.fetchMakes();
    } else if (dropdownType === 'model') {
      options = await this.fetchModels();
    }

    // Disable the dropdown if there are no options
    if (options.length === 0) {
      this.disableDropdown(dropdownToBePopulated);
    }

    this.populateDropdownOptions(dropdownToBePopulated, options);
    this.updateDropdownSelections();
    this.submitButtonEl.disabled = false;
  }

  populateDropdownOptions(dropdown, options) {
    // Reset options in the dropdown to be populated
    for (let i = dropdown.length - 1; i >= 1; i -= 1) {
      // eslint-disable-next-line no-param-reassign
      dropdown.options[i] = undefined;
    }

    // Populate the dropdown if there are options
    if (options.length > 0) {
      options.forEach(option => {
        dropdown.options.add(new Option(option, option));
      });

      this.enableDropdown(dropdown);
    }
  }

  updateDropdownSelections() {
    this.dropdowns.forEach(dropdown => {
      const { dataset, value } = dropdown;
      const { dropdownType } = dataset;

      if (dropdownType === 'make' && value !== 'Make') {
        this.selectedMake = value;
      } else if (dropdownType === 'model' && value !== 'Model') {
        this.selectedModel = value;
      } else if (dropdownType === 'type' && value !== 'Type') {
        this.selectedType = value;
      }
    });
  }

  updateSessionVariables() {
    sessionStorage.setItem('make', this.selectedMake);
    sessionStorage.setItem('model', this.selectedModel);
    sessionStorage.setItem('type', this.selectedType);
  }
}
